import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { EventImageApiResponse } from 'domain/event';
import { EventSeries } from 'domain/event/EventSeries';
import {
  mapEventSeriesDetailsFromApi,
  mapEventSeriesToUpdateEventSeriesApiPayload,
  mapEventSeriesWizardFormValuesToApi,
} from 'event/mappers';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { generateFormData } from 'store/utils';

import { newIdempotencyKey, omitUndefined } from './utils';

import type {
  ConvertSingleEventToSeriesPayload,
  CreateEventPayload,
  CreateEventSeriesResponse,
  DeleteEventsFromSeriesArgs,
  UpdateEventPayload,
  UpdateEventSeriesImagePayload,
  UpdateEventSeriesQuestionsPayload,
} from './types';

// See eventApi's withRetryOn502: the tag-category write rides on the series save, so a briefly
// unavailable tag service surfaces as 502. Retrying re-sends the same Idempotency-Key, so the backend
// resumes at the tag write instead of creating a second series.
const withRetryOn502 = retry(axiosBaseQuery({ baseURL: getApiBaseUrl('v2') }), {
  // maxRetries and retryCondition are mutually exclusive in RTK Query's RetryOptions union, so the
  // attempt cap lives inside the condition: 3 retries, 502 only.
  retryCondition: (error, _args, { attempt }) =>
    attempt <= 3 && (error as { status?: number } | undefined)?.status === 502,
});

export const eventSeriesApi = createApi({
  reducerPath: 'eventSeriesApi',
  baseQuery: axiosBaseQuery({ baseURL: getApiBaseUrl('v1') }),
  tagTypes: ['EVENT_SERIES'],
  endpoints: (builder) => ({
    getEventSeriesDetails: builder.query<EventSeries, { seriesId: number }>({
      keepUnusedDataFor: 0,
      query: ({ seriesId }) => ({
        url: `/eventSeries/${seriesId}`,
      }),
      transformResponse: mapEventSeriesDetailsFromApi,
      providesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    createEventSeries: builder.mutation<CreateEventSeriesResponse, CreateEventPayload>({
      queryFn: async ({ idempotencyKey, ...data }, api, extraOptions) => {
        const result = await withRetryOn502(
          {
            url: '/eventSeries',
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventSeriesWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: result.data as CreateEventSeriesResponse };
      },
    }),
    updateEventSeries: builder.mutation<void, UpdateEventPayload>({
      queryFn: async ({ seriesId, idempotencyKey, ...data }, api, extraOptions) => {
        const result = await withRetryOn502(
          {
            url: `/eventSeries/${seriesId}`,
            method: 'PUT',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventSeriesWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: undefined };
      },
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    convertSingleEventToSeries: builder.mutation<CreateEventSeriesResponse, ConvertSingleEventToSeriesPayload>({
      queryFn: async ({ id, idempotencyKey, ...data }, api, extraOptions) => {
        const result = await withRetryOn502(
          {
            url: `/eventSeries/withEvent/${id}`,
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventSeriesWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: result.data as CreateEventSeriesResponse };
      },
      invalidatesTags: (result) => (result ? [{ type: 'EVENT_SERIES', id: result.id }] : []),
    }),
    deleteEventSeries: builder.mutation<void, { seriesId: number }>({
      query: ({ seriesId }) => ({
        url: `/eventSeries/${seriesId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    // Removing occurrences carries no tag data of its own, but the v2 PUT requires a tagCategories array
    // and sending an empty one would clear the series' categories as a side effect. So this reads the
    // current vocabulary from a surviving event and passes it straight back, leaving it unchanged.
    deleteEventsFromSeries: builder.mutation<void, DeleteEventsFromSeriesArgs>({
      queryFn: async ({ eventSeries, eventIdsToRemove }, api, extraOptions, baseQuery) => {
        // Removing every occurrence is a series cancellation, which has nothing to do with tags.
        if (eventSeries.events.length === eventIdsToRemove.length) {
          const cancelled = await baseQuery({ url: `/eventSeries/${eventSeries.id}`, method: 'DELETE' });
          return cancelled.error ? { error: cancelled.error } : { data: undefined };
        }

        // The v2 series PUT requires tagCategories, and removing an occurrence must not change the
        // vocabulary — so read the series' current categories and pass them straight back. Any surviving
        // event carries them, since a series save writes the same set to every event in the series.
        // Retried on 502 like every other v3 event read: that endpoint fails rather than returning an
        // empty array when the tag service is unreadable, and a transient blip must not fail an
        // occurrence removal — which had no tag-service dependency at all before this change.
        // An absolute URL overrides the wrapper's v2 base.
        const survivor = eventSeries.events.find(({ id }) => !eventIdsToRemove.includes(id)) ?? eventSeries.events[0];
        const survivingEvent = await withRetryOn502(
          { url: `${getApiBaseUrl('v3')}/event/${survivor.id}` },
          api,
          extraOptions
        );
        if (survivingEvent.error) {
          return { error: survivingEvent.error };
        }
        const { tagCategories = [] } = survivingEvent.data as { tagCategories?: { name: string; tags: string[] }[] };

        const updated = await withRetryOn502(
          {
            url: `/eventSeries/${eventSeries.id}`,
            method: 'PUT',
            headers: { 'Idempotency-Key': newIdempotencyKey() },
            data: {
              ...mapEventSeriesToUpdateEventSeriesApiPayload({ data: eventSeries, eventIdsToRemove }),
              tagCategories,
            },
          },
          api,
          extraOptions
        );
        return updated.error ? { error: updated.error } : { data: undefined };
      },
      invalidatesTags: (_result, _error, { eventSeries }) => [{ type: 'EVENT_SERIES', id: eventSeries.id }],
    }),
    updateEventSeriesQuestions: builder.mutation<void, UpdateEventSeriesQuestionsPayload>({
      query: ({ seriesId, questions }) => ({
        url: `/eventSeries/${seriesId}/questions`,
        method: 'POST',
        data: questions,
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    postEventSeriesImage: builder.mutation<EventImageApiResponse, UpdateEventSeriesImagePayload>({
      query: ({ seriesId, ...params }) => ({
        url: `/eventSeries/${seriesId}/images`,
        headers: { 'Content-Type': 'multipart/form-data' },
        method: 'POST',
        data: generateFormData(omitUndefined(params)),
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
  }),
});

export const {
  useCreateEventSeriesMutation,
  useGetEventSeriesDetailsQuery,
  useDeleteEventsFromSeriesMutation,
  useUpdateEventSeriesQuestionsMutation,
} = eventSeriesApi;
